import * as THREE from "three";
import { math, type ProceduralBeat } from "@engine";
import type { LevelStreamer } from "../level/LevelStreamer";
import type { Segment, SweepInstance } from "../level/SegmentFactory";
import { APOTHEM } from "../config/tunnel";
import { shapeOf } from "../config/shapes";

// Distance from the tunnel axis to the sweep bar's center. APOTHEM is
// the inscribed circle radius (where the panel inner faces sit), so a
// bar at APOTHEM - 0.7 hovers slightly above the panel floor and the
// player ball (radius 0.45) reaches it on a graze.
const SWEEP_DIST = APOTHEM - 0.7;
// Tween duration between snare hits. Short enough to feel decisive,
// long enough for the player's eye to track the new target panel.
const TWEEN_S = 0.13;
// At rest the lerpAlpha sits at 1.0 (settled on prevPanel). On a snare
// we set prevPanel = current target, target = next, lerpAlpha = 0,
// then update() ramps lerpAlpha toward 1 over TWEEN_S.

const TMP_FROM_AXIS = new THREE.Vector3(0, 1, 0);
const TMP_OUTWARD = new THREE.Vector3();
const TMP_QUAT = new THREE.Quaternion();

interface SweepRuntime {
  prevPanel: number;
  targetPanel: number;
}

// Per-segment runtime state. Held off to the side because mutating
// SweepInstance shape directly would force every segment with .sweep
// to allocate the runtime fields up front.
const RUNTIME = new WeakMap<SweepInstance, SweepRuntime>();

// SweepFx: animates the kill-bar attached to every trick="sweep"
// segment. Each snare moves the bar to the next non-gap panel; in
// between snares the bar smoothly tweens so the player can track it.
//
// Mechanic: the player's job is to ensure the floor under them at the
// next snare is NOT the panel the bar is about to land on, AND not a
// gap. With 3-8 sides and at most 1-2 gaps, that means the player has
// to rotate every snare or two -- a tight rhythm-driven dodge.
export class SweepFx {
  private streamer: LevelStreamer;
  private unsub: (() => void) | null = null;

  constructor(streamer: LevelStreamer, beat: ProceduralBeat) {
    this.streamer = streamer;
    this.unsub = beat.onStep.on((step) => {
      if (!step.isSnare) return;
      this.advanceAll();
    });
  }

  // Per-frame: tween every active sweep mesh toward its target panel.
  // Cheap O(activeSegments-with-sweep), no allocations.
  update(unscaledDt: number): void {
    const segs = this.streamer.getActive();
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i]!;
      if (!seg.sweep) continue;
      this.ensureRuntime(seg);
      this.tickTween(seg, unscaledDt);
    }
  }

  // External reset (state transitions). Wipes per-segment runtime so
  // the next time those segments appear they re-init from a clean
  // first panel.
  clear(): void {
    // The WeakMap entries are reachable only via segment.sweep refs,
    // which detach() drops. Nothing to wipe here -- a no-op stub
    // for symmetry with the other FX systems' clear().
  }

  dispose(): void {
    if (this.unsub) {
      this.unsub();
      this.unsub = null;
    }
  }

  // Snare callback: every active sweep advances to its next non-gap
  // panel. We do this OUTSIDE update() so the move happens at audio
  // time (snare scheduling) rather than render time, so the bar
  // moves in lock-step with the music instead of drifting frame to
  // frame.
  private advanceAll(): void {
    const segs = this.streamer.getActive();
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i]!;
      if (!seg.sweep) continue;
      const rt = this.ensureRuntime(seg);
      const sides = shapeOf(seg.shape).sides;
      const next = nextNonGapPanel(rt.targetPanel, sides, seg.gaps);
      if (next === rt.targetPanel) continue; // no valid target
      rt.prevPanel = rt.targetPanel;
      rt.targetPanel = next;
      seg.sweep.lerpAlpha = 0;
    }
  }

  private ensureRuntime(seg: Segment): SweepRuntime {
    const sweep = seg.sweep!;
    let rt = RUNTIME.get(sweep);
    if (!rt) {
      const sides = shapeOf(seg.shape).sides;
      // Initial panel: the first non-gap panel starting at index 0.
      // Settled (lerpAlpha = 1) so the bar appears on the panel the
      // moment the segment becomes visible.
      const start = nextNonGapPanel(-1, sides, seg.gaps);
      rt = { prevPanel: start, targetPanel: start };
      RUNTIME.set(sweep, rt);
      sweep.panelIndex = start;
      sweep.lerpAlpha = 1;
      placeOnPanel(seg, sweep, start, start, 1);
    }
    return rt;
  }

  private tickTween(seg: Segment, unscaledDt: number): void {
    const sweep = seg.sweep!;
    const rt = RUNTIME.get(sweep);
    if (!rt) return;
    if (sweep.lerpAlpha < 1) {
      sweep.lerpAlpha = Math.min(1, sweep.lerpAlpha + unscaledDt / TWEEN_S);
    }
    const k = math.easeOutCubic(sweep.lerpAlpha);
    placeOnPanel(seg, sweep, rt.prevPanel, rt.targetPanel, k);
    sweep.panelIndex = sweep.lerpAlpha >= 0.5 ? rt.targetPanel : rt.prevPanel;
  }
}

// Returns the next panel index in the cycle direction skipping gaps.
// Returns the input if every panel is a gap (degenerate; segment would
// have no walls) so we never infinite-loop.
function nextNonGapPanel(
  current: number,
  sides: number,
  gaps: number[]
): number {
  const gapSet = new Set(gaps);
  for (let step = 1; step <= sides; step++) {
    const i = ((current + step) % sides + sides) % sides;
    if (!gapSet.has(i)) return i;
  }
  return Math.max(0, current);
}

// Compute angular position of the bar by lerping in 2D between the
// prev panel direction and the target panel direction, then mapping
// to (cx, cy) at SWEEP_DIST. Lerping in 2D (rather than just lerping
// the angle) gives a smooth straight-line slide instead of an arc
// which would briefly poke the bar through the cavity center.
function placeOnPanel(
  seg: Segment,
  sweep: SweepInstance,
  prevPanel: number,
  targetPanel: number,
  k: number
): void {
  const sides = shapeOf(seg.shape).sides;
  const aPrev = (prevPanel / sides) * math.TAU;
  const aNext = (targetPanel / sides) * math.TAU;
  // Interpolate via the SHORTER arc so the bar doesn't take the long
  // way around when crossing the wrap-around boundary.
  let delta = aNext - aPrev;
  if (delta > Math.PI) delta -= math.TAU;
  if (delta < -Math.PI) delta += math.TAU;
  const a = aPrev + delta * k;
  const dirX = Math.sin(a);
  const dirY = -Math.cos(a);
  sweep.mesh.position.set(dirX * SWEEP_DIST, dirY * SWEEP_DIST, 0);
  TMP_OUTWARD.set(dirX, dirY, 0);
  TMP_QUAT.setFromUnitVectors(TMP_FROM_AXIS, TMP_OUTWARD);
  sweep.mesh.quaternion.copy(TMP_QUAT);
}
